import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Upload, X, ExternalLink } from 'lucide-react';
import api, { getApiUrl } from '../../utils/api';

const API_URL = getApiUrl();

const ProjectForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title_tr: '',
    title_en: '',
    short_desc_tr: '',
    short_desc_en: '',
    detail_desc_tr: '',
    detail_desc_en: '',
    slug: '',
    image: '',
    link: '',
    technologies: '',
    client: '',
    year: '',
    active: true,
    order: 0
  });

  useEffect(() => {
    if (isEdit) {
      const fetchProject = async () => {
        setLoading(true);
        try {
          const response = await api.get(`/projects/${id}`);
          setFormData(response.data);
        } catch (error) {
          console.error('Error fetching project:', error);
          navigate('/admin/projects');
        } finally {
          setLoading(false);
        }
      };
      fetchProject();
    }
  }, [id, isEdit, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const generateSlug = () => {
    const slug = formData.title_tr
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    setFormData(prev => ({ ...prev, slug }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataFile = new FormData();
    formDataFile.append('file', file);

    try {
      const response = await api.post('/upload', formDataFile, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, image: response.data.url }));
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Görsel yüklenirken hata oluştu');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit) {
        await api.put(`/projects/${id}`, formData);
      } else {
        await api.post('/projects', formData);
      }
      navigate('/admin/projects');
    } catch (error) {
      console.error('Error saving project:', error);
      alert(error.response?.data?.detail || 'Proje kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-gray-400">Yükleniyor...</div>;
  }

  return (
    <div data-testid="admin-project-form">
      <div className="mb-8">
        <Link
          to="/admin/projects"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Projelere Dön
        </Link>
        <h1 className="text-3xl font-bold">{isEdit ? 'Proje Düzenle' : 'Yeni Proje'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Info */}
        <div className="p-6 rounded-2xl glass">
          <h2 className="text-xl font-bold mb-6">Temel Bilgiler</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Başlık (Türkçe) *</label>
              <input
                type="text"
                name="title_tr"
                value={formData.title_tr}
                onChange={handleChange}
                onBlur={() => !formData.slug && generateSlug()}
                required
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                         focus:border-blue-500 focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Title (English) *</label>
              <input
                type="text"
                name="title_en"
                value={formData.title_en}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                         focus:border-blue-500 focus:outline-none transition-colors"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm text-gray-400 mb-2">URL Slug *</label>
            <div className="flex gap-2">
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                required
                className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                         focus:border-blue-500 focus:outline-none transition-colors"
              />
              <button
                type="button"
                onClick={generateSlug}
                className="px-4 py-3 rounded-xl glass hover:bg-white/10 transition-colors"
              >
                Oluştur
              </button>
            </div>
          </div>
        </div>

        {/* Project Details */}
        <div className="p-6 rounded-2xl glass">
          <h2 className="text-xl font-bold mb-6">Proje Detayları</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2 flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                Proje Linki (URL)
              </label>
              <input
                type="url"
                name="link"
                value={formData.link || ''}
                onChange={handleChange}
                placeholder="https://example.com"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                         focus:border-blue-500 focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Müşteri / Firma</label>
              <input
                type="text"
                name="client"
                value={formData.client || ''}
                onChange={handleChange}
                placeholder="Firma Adı"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                         focus:border-blue-500 focus:outline-none transition-colors"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Kullanılan Teknolojiler</label>
              <input
                type="text"
                name="technologies"
                value={formData.technologies || ''}
                onChange={handleChange}
                placeholder="React, Node.js, MongoDB..."
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                         focus:border-blue-500 focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Proje Yılı</label>
              <input
                type="text"
                name="year"
                value={formData.year || ''}
                onChange={handleChange}
                placeholder="2024"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                         focus:border-blue-500 focus:outline-none transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Short Descriptions */}
        <div className="p-6 rounded-2xl glass">
          <h2 className="text-xl font-bold mb-6">Kısa Açıklamalar</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Kısa Açıklama (Türkçe) *</label>
              <textarea
                name="short_desc_tr"
                value={formData.short_desc_tr}
                onChange={handleChange}
                required
                rows={3}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                         focus:border-blue-500 focus:outline-none transition-colors resize-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Short Description (English) *</label>
              <textarea
                name="short_desc_en"
                value={formData.short_desc_en}
                onChange={handleChange}
                required
                rows={3}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                         focus:border-blue-500 focus:outline-none transition-colors resize-none"
              />
            </div>
          </div>
        </div>

        {/* Detail Descriptions */}
        <div className="p-6 rounded-2xl glass">
          <h2 className="text-xl font-bold mb-6">Detaylı Açıklamalar (HTML destekli)</h2>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Detaylı Açıklama (Türkçe) *</label>
              <textarea
                name="detail_desc_tr"
                value={formData.detail_desc_tr}
                onChange={handleChange}
                required
                rows={8}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                         focus:border-blue-500 focus:outline-none transition-colors resize-none font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Detail Description (English) *</label>
              <textarea
                name="detail_desc_en"
                value={formData.detail_desc_en}
                onChange={handleChange}
                required
                rows={8}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                         focus:border-blue-500 focus:outline-none transition-colors resize-none font-mono text-sm"
              />
            </div>
          </div>
        </div>

        {/* Image */}
        <div className="p-6 rounded-2xl glass">
          <h2 className="text-xl font-bold mb-6">Proje Görseli</h2>
          
          {formData.image && (
            <div className="relative inline-block mb-4">
              <img
                src={formData.image.startsWith('http') ? formData.image : `${API_URL}${formData.image}`}
                alt="Preview"
                className="w-64 h-40 object-cover rounded-xl"
              />
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          <label className="flex items-center gap-3 px-6 py-4 rounded-xl glass cursor-pointer hover:bg-white/10 transition-colors">
            <Upload className="w-5 h-5 text-gray-400" />
            <span className="text-gray-400">Görsel Yükle</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </label>
        </div>

        {/* Settings */}
        <div className="p-6 rounded-2xl glass">
          <h2 className="text-xl font-bold mb-6">Ayarlar</h2>
          
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
                className="w-5 h-5 rounded bg-white/10 border-white/20 text-blue-500 focus:ring-blue-500"
              />
              <span>Aktif</span>
            </label>
            
            <div className="flex items-center gap-3">
              <label className="text-gray-400">Sıra:</label>
              <input
                type="number"
                name="order"
                value={formData.order}
                onChange={handleChange}
                className="w-20 px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                         focus:border-blue-500 focus:outline-none transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Link
            to="/admin/projects"
            className="px-6 py-3 rounded-xl glass hover:bg-white/10 transition-colors"
          >
            İptal
          </Link>
          <motion.button
            type="submit"
            disabled={saving}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl font-semibold 
                     flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </motion.button>
        </div>
      </form>
    </div>
  );
};

export default ProjectForm;
